import { useCallback, useMemo } from 'react';

import { Media } from '@media-viewer';
import { useAtom, useAtomValue } from 'jotai';

import { cameraViews } from '@core';
import { useTranslate } from '@i18n';
import { useUpdateUserSettings } from '@queries';
import {
    activeUserSettingsAtom,
    activeVideoAtom,
    cameraViewAtom,
    cameraViewMapAtom,
    comparisonVideoAtom,
    defaultCameraAtom,
    selectedCameraAtom,
} from '@store';
import { ActionButton, Box, Heading3, Heading4, Tabs, u } from '@ui';

import * as css from './MobileVideos.css';

const DEFAULT_CAMERA_VIEW = 1;

const MobileVideoTabs = () => {
    const [cameraView, setCameraView] = useAtom(cameraViewAtom);
    const cameraViewMap = useAtomValue(cameraViewMapAtom);
    const t = useTranslate();
    const tabLabels = useMemo(() => {
        if (!Object.entries(cameraViewMap).length) return [];
        return cameraViews.map((view) => {
            const camera = cameraViewMap[view];
            return { label: t(`camera_angle.${camera.name}`), id: String(view) };
        });
    }, [cameraViewMap, t]);

    return <Tabs items={tabLabels} activeId={String(cameraView)} onClick={(key) => setCameraView(Number(key))} />;
};

const MobileVideoPlayer = () => {
    const selectedCamera = useAtomValue(selectedCameraAtom);
    const defaultCamera = useAtomValue(defaultCameraAtom);
    const cameraView = selectedCamera?.view ?? defaultCamera?.view ?? DEFAULT_CAMERA_VIEW;

    const userSettings = useAtomValue(activeUserSettingsAtom);
    const { showComparison, userId } = userSettings;
    const activeVideos = useAtomValue(activeVideoAtom);
    const comparisonVideos = useAtomValue(comparisonVideoAtom);
    const hasComparison = !!comparisonVideos && comparisonVideos.length > 0;
    const { mutateAsync: upsertUserSettings } = useUpdateUserSettings(userId);

    const t = useTranslate();

    const video = useMemo(() => {
        const source = showComparison && hasComparison ? comparisonVideos : activeVideos;
        return source?.find((video) => video.view === cameraView);
    }, [activeVideos, comparisonVideos, hasComparison, showComparison, cameraView]);

    const handleToggleComparison = useCallback(
        () => upsertUserSettings({ showComparison: !userSettings.showComparison }),
        [upsertUserSettings, userSettings.showComparison],
    );

    if (!video) return null;

    const label = showComparison && hasComparison ? t('media_player.comparison') : t('media_player.current');

    return (
        <Media.Layout rows={1} cols={1}>
            <Media.Cell
                key={video.cameraName}
                type="video"
                src={video.url}
                origin="current"
                overlay={
                    <>
                        <Box p={u(1)} display="flex" flexDirection="column" gap={u(0.5)}>
                            <Heading4 color="inverse">{label}</Heading4>
                            <Heading3 color="inverse">{t(`camera_angle.${video.cameraName}`)}</Heading3>
                        </Box>
                        <div className={css.actionButtonPosition}>
                            <ActionButton
                                icon="comparison"
                                variant={showComparison ? 'primary' : 'default'}
                                buttonSize="medium"
                                onClick={handleToggleComparison}
                                aria-label={showComparison ? 'Show active swing' : 'Show comparison swing'}
                            />
                        </div>
                    </>
                }
            />
        </Media.Layout>
    );
};

export function MobileVideos() {
    return (
        <div className={css.container}>
            <MobileVideoTabs />
            <div className={css.videoWrapper}>
                <MobileVideoPlayer />
            </div>
        </div>
    );
}
